import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fileUrl, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Pencil, Trash2, Plus } from "lucide-react";

const AdminCoins = () => {
  const [coins, setCoins] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/coins")
      .then(({ data }) => setCoins(data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (coin) => {
    if (!window.confirm(`Delete "${coin.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/coins/${coin.id}`);
      toast.success("Coin removed from catalog");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
        <div>
          <div className="coin-tag mb-2">Catalog Management</div>
          <h1 className="font-display text-4xl tracking-tight">Coins</h1>
        </div>
        <Link to="/admin/coins/new" className="bg-[#D4AF37] text-black font-medium px-6 py-3 inline-flex items-center gap-2 hover:bg-[#F1C40F] transition-colors" data-testid="add-coin-btn">
          <Plus className="w-4 h-4" strokeWidth={2} /> New Coin
        </Link>
      </div>

      <div className="border border-[#F3EFE0]/10 bg-[#0A0A0A] overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-coins-table">
          <thead className="border-b border-[#F3EFE0]/10">
            <tr className="text-left">
              <th className="px-4 py-3 coin-tag">Image</th>
              <th className="px-4 py-3 coin-tag">Name</th>
              <th className="px-4 py-3 coin-tag">Era / Metal</th>
              <th className="px-4 py-3 coin-tag">Price</th>
              <th className="px-4 py-3 coin-tag">Status</th>
              <th className="px-4 py-3 coin-tag text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F3EFE0]/5">
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-[#6B6A65]">Loading…</td></tr>
            ) : coins.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-[#6B6A65]">No coins in the catalog yet. <Link to="/admin/coins/new" className="text-[#D4AF37]">Add the first one →</Link></td></tr>
            ) : (
              coins.map((coin) => (
                <tr key={coin.id} className="hover:bg-[#121212] transition-colors" data-testid={`coin-row-${coin.id}`}>
                  <td className="px-4 py-3">
                    {coin.image_url ? (
                      <img src={fileUrl(coin.image_url)} alt={coin.name} className="w-12 h-12 object-cover bg-black" />
                    ) : (
                      <div className="w-12 h-12 bg-[#121212] border border-[#F3EFE0]/10 grid place-items-center text-[#6B6A65] text-xs">—</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-display text-lg text-[#F3EFE0]">{coin.name}</div>
                    <div className="text-xs text-[#6B6A65]">{coin.year || ""}</div>
                  </td>
                  <td className="px-4 py-3 text-[#A19D94]">{coin.era || "—"} / {coin.metal || "—"}</td>
                  <td className="px-4 py-3 font-mono text-[#D4AF37]">${Number(coin.price).toLocaleString()}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 flex-wrap">
                      {coin.featured && <span className="coin-tag border border-[#D4AF37]/40 text-[#D4AF37] px-2 py-0.5">Featured</span>}
                      {coin.sold ? (
                        <span className="coin-tag border border-[#5C2B29]/40 text-[#C5A059] px-2 py-0.5">Sold</span>
                      ) : (
                        <span className="coin-tag border border-[#2E4B31]/40 text-[#A19D94] px-2 py-0.5">Available</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2 justify-end">
                      <Link to={`/admin/coins/${coin.id}/edit`} className="p-2 text-[#A19D94] hover:text-[#D4AF37] border border-[#F3EFE0]/10 hover:border-[#D4AF37]/40 transition-colors" data-testid={`edit-coin-${coin.id}`}>
                        <Pencil className="w-4 h-4" strokeWidth={1.5} />
                      </Link>
                      <button onClick={() => handleDelete(coin)} className="p-2 text-[#A19D94] hover:text-[#C5A059] border border-[#F3EFE0]/10 hover:border-[#5C2B29]/60 transition-colors" data-testid={`delete-coin-${coin.id}`}>
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminCoins;
